import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Button,
  ButtonGroup,
  Badge,
  Switch,
  Select,
  FormControl,
  FormLabel,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { AddIcon, RepeatIcon } from "@chakra-ui/icons";
import { filterItems, countNeedsMyReview, copyTextFor, inChannel, taskToItem } from "./tasksModel";
import TaskRow from "./TaskRow";
import TaskBoard from "./TaskBoard";
import { BOARD_COLUMNS, boardColumnFor, boardColumns, viewFor } from "./boardModel";
import useWorkItemSummaries from "./useWorkItemSummaries";
import { summaryKey } from "../../utils/workItemRefs";
import { fetchTasksInChannel } from "../../services/messagingService";

// The channel picked last, per user, so the Tasks tab reopens on it. Browser
// storage can be missing or throw (private windows); then it just isn't kept.
const channelKey = (userId) => `tasks.channel.${userId}`;
function readSavedChannel(userId) {
  try {
    return (userId && window.localStorage.getItem(channelKey(userId))) || "";
  } catch {
    return "";
  }
}
function saveChannel(userId, channelId) {
  try {
    if (!userId) return;
    if (channelId) window.localStorage.setItem(channelKey(userId), channelId);
    else window.localStorage.removeItem(channelKey(userId));
  } catch {
    // Not kept; the pick still applies until the tab closes.
  }
}

const channelLabel = (c) => (c.channel_type === "dm" ? c.dm_label || "Direct message" : `# ${c.name}`);

export default function TasksPanel({
  items,
  // The channels I'm in: the picker shows one's tasks (assigned to and by its members).
  channels = [],
  loading,
  currentUser,
  onItemChanged,
  onReload,
  onNewTask,
  onAssignOrder,
  focus = null,
  onFocusHandled,
}) {
  const [view, setView] = useState("mine");
  const [showDone, setShowDone] = useState(false);
  // The board (Jira-style columns) is the default; List is the plain list.
  const [layout, setLayout] = useState("board");
  const toast = useToast();

  // "" = My tasks; otherwise a channel I'm in: its tasks (assigned to AND by
  // its members, loaded here) and the loaded orders narrowed the same way.
  const [pickedChannelId, setPickedChannelId] = useState(() => readSavedChannel(currentUser?.id));
  // Another user signing in on this browser gets their own pick.
  useEffect(() => { setPickedChannelId(readSavedChannel(currentUser?.id)); }, [currentUser?.id]);
  const setChannelId = (id) => {
    setPickedChannelId(id);
    saveChannel(currentUser?.id, id);
  };
  // The pick applies once it's one of my channels (not before the list
  // loads, nor after I've left it — then it's My tasks, and kept for if I rejoin).
  const channelId = pickedChannelId && (channels || []).some((c) => c.id === pickedChannelId) ? pickedChannelId : "";
  const [channelData, setChannelData] = useState(null); // { channelId, tasks: item[], memberIds: string[] }
  const sortedChannels = useMemo(
    () => [...(channels || [])].sort((a, b) => channelLabel(a).localeCompare(channelLabel(b))),
    [channels],
  );
  // Reloaded with every Tasks reload (`items` changes), so it stays as fresh.
  useEffect(() => {
    if (!channelId || !currentUser?.id) return undefined;
    let cancelled = false;
    fetchTasksInChannel(channelId, currentUser.id)
      .then((res) => {
        if (cancelled) return;
        setChannelData({ channelId, tasks: (res?.tasks || []).map(taskToItem), memberIds: res?.member_ids || [] });
      })
      .catch((err) => {
        console.warn("Tasks: failed to load the channel's tasks", err);
        // Keep a loaded copy of this channel; on a first load, show it empty with the reason.
        if (cancelled) return;
        setChannelData((d) => (d?.channelId === channelId ? d : { channelId, tasks: [], memberIds: [], error: err?.message || "Couldn't load" }));
      });
    return () => { cancelled = true; };
  }, [channelId, currentUser?.id, items]);
  const channelReady = !!channelId && channelData?.channelId === channelId;
  const scoped = useMemo(() => {
    if (!channelReady) return null;
    const members = new Set(channelData.memberIds);
    return [...channelData.tasks, ...(items || []).filter((i) => i.kind === "order" && inChannel(i, members))];
  }, [channelReady, channelData, items]);
  const shownItems = useMemo(() => (channelId ? scoped || [] : items), [channelId, scoped, items]);
  const loadingShown = channelId ? !channelReady : loading && items.length === 0;

  // A change made here also updates the channel's copy until it reloads.
  const handleItemChanged = (changed) => {
    onItemChanged(changed);
    setChannelData((d) => d && { ...d, tasks: d.tasks.map((t) => (t.key === changed.key ? changed : t)) });
  };

  // The board holds everything assigned to or by me (or the picked channel's);
  // "Assigned to me / by me" and "Show completed" are the list's filters.
  const columns = useMemo(
    () => (layout === "board"
      ? boardColumns(shownItems, { userId: currentUser?.id, memberIds: channelReady ? channelData.memberIds : undefined })
      : null),
    [layout, shownItems, currentUser?.id, channelReady, channelData],
  );
  const visible = useMemo(
    () => (columns
      ? BOARD_COLUMNS.flatMap((c) => columns[c.id])
      : filterItems(shownItems, {
        userId: currentUser?.id, view: channelId ? "channel" : view, showDone,
        memberIds: channelReady ? channelData.memberIds : undefined,
      })),
    [columns, shownItems, currentUser?.id, view, showDone, channelId, channelReady, channelData],
  );
  // Each row's 💬 count and, for a rejected one, the reason it was given.
  const summaries = useWorkItemSummaries(visible);

  const needsReviewCount = countNeedsMyReview(items, currentUser?.id);

  const [highlightKey, setHighlightKey] = useState(null);
  const sawLoadingRef = useRef(false);
  const focusKey = focus ? `${focus.kind}:${focus.id}` : null;

  // Reset to the CURRENT loading state, not unconditionally false: if a new
  // focus arrives while a reload is already in flight, there's no fresh
  // true→false edge left for the effect below to observe, so starting the
  // ref at `loading` (already true) lets the very next false catch it
  // instead of stalling until the next 30s poll.
  // Only re-run when the focus itself changes; `loading` is read, not watched, here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { sawLoadingRef.current = loading; }, [focusKey]);
  useEffect(() => { if (loading) sawLoadingRef.current = true; }, [loading]);

  // "Open in Tasks": show the right view for the focused row, reveal it if
  // completed, then highlight it. A task that isn't mine (either way) is only
  // declared missing after a load cycle, so a slow first load doesn't lie.
  useEffect(() => {
    if (!focusKey) return;
    const me = currentUser?.id;
    const item = (items || []).find((i) => i.key === focusKey);
    const involved = item && (item.assignedTo === me || item.assignedBy === me);
    if (involved) {
      setChannelId("");
      setView(item.assignedTo === me ? "mine" : "assigned");
      // Completed and cancelled items only show with "Show completed".
      if (item.done || item.cancelled) setShowDone(true);
      // The board holds open work and the last week's completions; anything
      // else is found in the list.
      if (!boardColumnFor(item, me)) setLayout("list");
      setHighlightKey(item.key);
      onFocusHandled?.();
      return;
    }
    if (!loading && sawLoadingRef.current) {
      toast({ title: "That task isn't assigned to or by you", status: "info", duration: 3000 });
      onFocusHandled?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey, items, loading, currentUser?.id]);

  useEffect(() => {
    if (!highlightKey) return undefined;
    const t = setTimeout(() => setHighlightKey(null), 3000);
    return () => clearTimeout(t);
  }, [highlightKey]);

  const handleCopy = async (item) => {
    try {
      await navigator.clipboard.writeText(copyTextFor(item));
      toast({ title: "Copied", status: "success", duration: 1500 });
    } catch (err) {
      toast({ title: "Couldn't copy", description: err?.message, status: "error", duration: 4000 });
    }
  };

  return (
    <Flex direction="column" h="100%" w="100%">
      <Flex px={4} py={3} borderBottom="1px solid" borderColor="gray.200" gap={3} align="center" flexWrap="wrap">
        <ButtonGroup size="sm" isAttached variant="outline">
          {[["board", "Board"], ["list", "List"]].map(([id, label]) => (
            <Button key={id} onClick={() => setLayout(id)} colorScheme={layout === id ? "blue" : "gray"}
                    variant={layout === id ? "solid" : "outline"} aria-pressed={layout === id}>
              {label}
            </Button>
          ))}
        </ButtonGroup>
        <Select size="sm" w="auto" maxW="220px" value={channelId} onChange={(e) => setChannelId(e.target.value)}
                aria-label="Whose tasks" data-testid="tasks-channel-picker">
          <option value="">My tasks</option>
          {sortedChannels.map((c) => (
            <option key={c.id} value={c.id}>{channelLabel(c)}</option>
          ))}
        </Select>
        {/* The list's filters. */}
        {layout === "list" && (
          <>
            {!channelId && (
            <ButtonGroup size="sm" isAttached variant="outline">
              <Button onClick={() => setView("mine")} colorScheme={view === "mine" ? "blue" : "gray"} variant={view === "mine" ? "solid" : "outline"}>
                Assigned to me
              </Button>
              <Button
                onClick={() => setView("assigned")}
                colorScheme={view === "assigned" ? "blue" : "gray"}
                variant={view === "assigned" ? "solid" : "outline"}
              >
                Assigned by me
                {needsReviewCount > 0 && (
                  <Badge ml={2} colorScheme="red" borderRadius="full">
                    {needsReviewCount}
                  </Badge>
                )}
              </Button>
            </ButtonGroup>
            )}
            <FormControl display="flex" alignItems="center" w="auto">
              <Switch id="tasks-show-done" size="sm" isChecked={showDone} onChange={(e) => setShowDone(e.target.checked)} />
              <FormLabel htmlFor="tasks-show-done" mb={0} ml={2} fontSize="sm">
                Show completed
              </FormLabel>
            </FormControl>
          </>
        )}
        <HStack spacing={2} ml="auto">
          <Button size="sm" variant="ghost" leftIcon={<RepeatIcon />} onClick={onReload} isLoading={loading}>
            Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={onAssignOrder}>
            Assign order
          </Button>
          <Button size="sm" colorScheme="blue" leftIcon={<AddIcon />} onClick={onNewTask}>
            New task
          </Button>
        </HStack>
      </Flex>
      {layout === "board" ? (
        <Box flex="1" minH={0} p={4}>
          {loadingShown ? (
            <HStack color="gray.500">
              <Spinner size="sm" />
              <Text>Loading tasks…</Text>
            </HStack>
          ) : (
            <TaskBoard
              items={shownItems}
              columns={columns}
              currentUserId={currentUser?.id}
              currentUserSchema={currentUser?.schema}
              summaries={summaries}
              highlightKey={highlightKey}
              onItemChanged={handleItemChanged}
              onCopy={handleCopy}
            />
          )}
        </Box>
      ) : (
        <Box flex="1" overflowY="auto" p={4}>
          {loadingShown ? (
            <HStack color="gray.500">
              <Spinner size="sm" />
              <Text>Loading tasks…</Text>
            </HStack>
          ) : visible.length === 0 ? (
            <Text color="gray.400" textAlign="center" mt={10}>
              {channelId
                ? channelData?.error ? `Couldn't load this channel's tasks: ${channelData.error}` : "No tasks between this channel's members"
                : view === "mine"
                ? showDone
                  ? "Nothing assigned to you yet"
                  : "You're all caught up"
                : "You haven't assigned anything yet"}
            </Text>
          ) : (
            <VStack align="stretch" spacing={2}>
              {visible.map((item) => (
                <TaskRow
                  key={item.key}
                  item={item}
                  view={channelId ? viewFor(item, currentUser?.id) : view}
                  currentUserId={currentUser?.id}
                  currentUserSchema={currentUser?.schema}
                  summary={summaries[summaryKey(item)]}
                  onItemChanged={handleItemChanged}
                  onCopy={handleCopy}
                  highlighted={item.key === highlightKey}
                />
              ))}
            </VStack>
          )}
        </Box>
      )}
    </Flex>
  );
}
